/**
 * Helpers to build the `service_resources_map` payload accepted by
 * `POST /api/company-user-role/register` and `PUT /api/company-user-role/:id`.
 *
 * The backend uses this map as a journal (`service_assignments`) tracking which
 * resource comes from which service. It is only relevant for beneficiaries
 * (`company_user_role === 5`).
 *
 * NOTE: The backend does NOT sync this map with the classic relations
 * (`books`, `tests`, `tests_to_do`, `authorized_games`, `authorized_ebooks`).
 * Callers must keep sending those classic lists with their final values
 * (typically the union of resources across all attached services).
 */

export interface ServiceResourceIds {
  upper_hierarchy?: number;
  tests_to_do?: number[];
  book_template_ids?: number[];
  major_test_ids?: number[];
  authorized_games?: number[];
  authorized_ebooks?: number[];
  authorized_minor_tests?: number[];
  authorized_major_tests?: number[];
}

export type ServiceResourcesMap = Record<string, ServiceResourceIds>;

const toId = (value: any): number | null => {
  if (value == null) {
    return null;
  }
  const id = typeof value === 'object' ? Number(value.id) : Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const toIdList = (values?: Array<any>): number[] =>
  (values || []).map(toId).filter((id): id is number => id !== null);

/**
 * Build a `service_resources_map` entry for a single service.
 * Unknown / empty categories are dropped so the payload stays minimal.
 */
export const buildServiceResourcesEntry = (resources: {
  upperHierarchy?: number | string | { id?: number } | null;
  minorTests?: Array<{ id: number }>;
  books?: Array<{ id: number }>;
  majorTests?: Array<{ id: number }>;
  games?: Array<{ id: number }>;
  ebooks?: Array<{ id: number }>;
}): ServiceResourceIds => {
  const entry: ServiceResourceIds = {};
  const upper_hierarchy = toId(resources.upperHierarchy);
  if (upper_hierarchy !== null) {
    entry.upper_hierarchy = upper_hierarchy;
  }
  const tests_to_do = toIdList(resources.minorTests);
  if (tests_to_do.length) {
    entry.tests_to_do = tests_to_do;
  }
  const book_template_ids = toIdList(resources.books);
  if (book_template_ids.length) {
    entry.book_template_ids = book_template_ids;
  }
  const major_test_ids = toIdList(resources.majorTests);
  if (major_test_ids.length) {
    entry.major_test_ids = major_test_ids;
  }
  const authorized_games = toIdList(resources.games);
  if (authorized_games.length) {
    entry.authorized_games = authorized_games;
  }
  const authorized_ebooks = toIdList(resources.ebooks);
  if (authorized_ebooks.length) {
    entry.authorized_ebooks = authorized_ebooks;
  }
  return entry;
};

/**
 * Build a full `service_resources_map` for a single service.
 * The map key is the service id (as required by the backend contract).
 * Returns undefined if there is no service (nothing to attribute).
 */
export const buildSingleServiceResourcesMap = (
  serviceId: number | string | null | undefined,
  resources: Parameters<typeof buildServiceResourcesEntry>[0]
): ServiceResourcesMap | undefined => {
  const id = toId(serviceId);
  if (!id) {
    return undefined;
  }
  return { [String(id)]: buildServiceResourcesEntry(resources) };
};

/**
 * Raw shape of an entry inside the `service_assignments` component
 * returned by GET user (or seeded on the fly by the backend for legacy users).
 * We accept both attribute shapes (Strapi v4 relation) and flat shapes.
 */
export interface ServiceAssignmentEntry {
  service?: any;
  upper_hierarchy?: any;
  tests_to_do?: any[];
  book_templates?: any[];
  major_tests?: any[];
  games?: any[];
  ebooks?: any[];
  // Alternative naming that the backend may emit:
  book_template_ids?: any[];
  major_test_ids?: any[];
  authorized_games?: any[];
  authorized_ebooks?: any[];
}

const pickResourceList = (
  entry: ServiceAssignmentEntry,
  keys: Array<keyof ServiceAssignmentEntry>
): any[] => {
  const match = keys
    .map((key) => entry[key])
    .find((value): value is any[] => Array.isArray(value) && value.length > 0);
  return match || [];
};

/** Prefer the populated relation even when empty, so we never fall back to a union list. */
const pickPresentList = (
  entry: ServiceAssignmentEntry,
  primary: keyof ServiceAssignmentEntry,
  fallback: keyof ServiceAssignmentEntry
): any[] => {
  if (Array.isArray(entry[primary])) {
    return entry[primary] as any[];
  }
  if (Array.isArray(entry[fallback])) {
    return entry[fallback] as any[];
  }
  return [];
};

export type AssignmentServiceGroup = {
  id: number;
  name: string;
  gameIds: Set<number>;
  ebookIds: Set<number>;
  bookIds: Set<number>;
};

const assignmentList = (assignmentsRaw: any): any[] =>
  Array.isArray(assignmentsRaw)
    ? assignmentsRaw
    : Array.isArray(assignmentsRaw?.data)
    ? assignmentsRaw.data
    : [];

const readAssignmentEntry = (raw: any): ServiceAssignmentEntry | null => {
  if (!raw) {
    return null;
  }
  const source = raw.attributes ? { id: raw.id, ...raw.attributes } : raw;
  const serviceRaw = source.service?.data ?? source.service;
  const serviceEntity = Array.isArray(serviceRaw) ? serviceRaw[0] : serviceRaw;
  const service = serviceEntity?.attributes
    ? { id: serviceEntity.id, ...serviceEntity.attributes }
    : serviceEntity;
  const hierarchyRaw = source.upper_hierarchy?.data ?? source.upper_hierarchy;
  const hierarchyEntity = Array.isArray(hierarchyRaw) ? hierarchyRaw[0] : hierarchyRaw;
  const upper_hierarchy = hierarchyEntity?.attributes
    ? { id: hierarchyEntity.id, ...hierarchyEntity.attributes }
    : hierarchyEntity;
  return { ...source, service, upper_hierarchy };
};

/** Flatten `service_assignments` into per-prestation resource id sets + labels. */
export const listAssignmentServiceGroups = (
  assignmentsRaw: any
): AssignmentServiceGroup[] => {
  const groups: AssignmentServiceGroup[] = [];
  assignmentList(assignmentsRaw).forEach(raw => {
    const entry = readAssignmentEntry(raw);
    if (!entry) {
      return;
    }
    const serviceId = toId(entry.service);
    if (!serviceId) {
      return;
    }
    const serviceName =
      (typeof entry.service === 'object' &&
        (entry.service.name || entry.service.attributes?.name)) ||
      `Service ${serviceId}`;
    groups.push({
      id: serviceId,
      name: serviceName,
      gameIds: new Set(toIdList(pickPresentList(entry, 'games', 'authorized_games'))),
      ebookIds: new Set(toIdList(pickPresentList(entry, 'ebooks', 'authorized_ebooks'))),
      bookIds: new Set(toIdList(pickPresentList(entry, 'book_templates', 'book_template_ids'))),
    });
  });
  return groups;
};

export const assignmentJournalHasResources = (groups: AssignmentServiceGroup[]): boolean =>
  groups.some(
    group => group.gameIds.size > 0 || group.ebookIds.size > 0 || group.bookIds.size > 0
  );

/** Merge per-service ids from `service_resources_map` into the journal groups. */
export const mergeResourcesMapIntoAssignmentGroups = (
  groups: AssignmentServiceGroup[],
  resourcesMap?: ServiceResourcesMap | null
): AssignmentServiceGroup[] => {
  if (!resourcesMap || typeof resourcesMap !== 'object') {
    return groups;
  }

  const byId = new Map<number, AssignmentServiceGroup>();
  groups.forEach(group => {
    byId.set(group.id, {
      ...group,
      gameIds: new Set(group.gameIds),
      ebookIds: new Set(group.ebookIds),
      bookIds: new Set(group.bookIds),
    });
  });

  Object.entries(resourcesMap).forEach(([key, entry]) => {
    const serviceId = toId(key);
    if (!serviceId || !entry) {
      return;
    }
    const current = byId.get(serviceId) || {
      id: serviceId,
      name: `Service ${serviceId}`,
      gameIds: new Set<number>(),
      ebookIds: new Set<number>(),
      bookIds: new Set<number>(),
    };
    toIdList(entry.authorized_games).forEach(id => current.gameIds.add(id));
    toIdList(entry.authorized_ebooks).forEach(id => current.ebookIds.add(id));
    toIdList(entry.book_template_ids).forEach(id => current.bookIds.add(id));
    byId.set(serviceId, current);
  });

  return Array.from(byId.values());
};

/**
 * Normalize the `service_assignments` component from a user object into a
 * `ServiceResourcesMap` keyed by service id.
 */
export const buildServiceResourcesMapFromAssignments = (
  assignmentsRaw: any
): ServiceResourcesMap => {
  const map: ServiceResourcesMap = {};
  assignmentList(assignmentsRaw).forEach((raw: any) => {
    const entry = readAssignmentEntry(raw);
    if (!entry) {
      return;
    }
    const serviceId = toId(entry.service);
    if (!serviceId) {
      return;
    }
    map[String(serviceId)] = buildServiceResourcesEntry({
      upperHierarchy: entry.upper_hierarchy,
      minorTests: pickResourceList(entry, ['tests_to_do']),
      books: pickResourceList(entry, ['book_templates', 'book_template_ids']),
      majorTests: pickResourceList(entry, ['major_tests', 'major_test_ids']),
      games: pickResourceList(entry, ['games', 'authorized_games']),
      ebooks: pickResourceList(entry, ['ebooks', 'authorized_ebooks']),
    });
  });
  return map;
};

/** Return a shallow copy of the map without the entry for `serviceId`. */
export const removeServiceFromMap = (
  map: ServiceResourcesMap,
  serviceId: number | string
): ServiceResourcesMap => {
  const key = String(toId(serviceId) ?? serviceId);
  const next: ServiceResourcesMap = {};
  Object.entries(map).forEach(([id, entry]) => {
    if (id !== key) {
      next[id] = entry;
    }
  });
  return next;
};

export interface ResourceRelationLists {
  tests_to_do: number[];
  book_template_ids: number[];
  major_test_ids: number[];
  authorized_games: number[];
  authorized_ebooks: number[];
}

const uniqueIds = (values: number[]): number[] => Array.from(new Set(values));

/**
 * Compute the union of resource ids across all service entries of the map.
 * Used to rebuild the classic relations (`books`, `tests`, `tests_to_do`,
 * `authorized_games`, `authorized_ebooks`) after adding or removing a service.
 */
export const computeUnionRelationsFromMap = (
  map: ServiceResourcesMap
): ResourceRelationLists => {
  const acc: ResourceRelationLists = {
    tests_to_do: [],
    book_template_ids: [],
    major_test_ids: [],
    authorized_games: [],
    authorized_ebooks: [],
  };
  Object.values(map).forEach((entry) => {
    acc.tests_to_do.push(...(entry.tests_to_do || []));
    acc.book_template_ids.push(...(entry.book_template_ids || []));
    acc.major_test_ids.push(...(entry.major_test_ids || []));
    acc.authorized_games.push(...(entry.authorized_games || []));
    acc.authorized_ebooks.push(...(entry.authorized_ebooks || []));
  });
  return {
    tests_to_do: uniqueIds(acc.tests_to_do),
    book_template_ids: uniqueIds(acc.book_template_ids),
    major_test_ids: uniqueIds(acc.major_test_ids),
    authorized_games: uniqueIds(acc.authorized_games),
    authorized_ebooks: uniqueIds(acc.authorized_ebooks),
  };
};
